import { useIsOnline } from 'hooks';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import OnlineIcon from './icons/online.svg';
import OfflineIcon from './icons/offline.svg';
import CompletedIcon from './icons/completed.svg';
import { Icon } from '@material-ui/core';
import { db, SyncManager } from 'db';
import { useGetOfflineFirstSectionsQuery } from 'services/api/vehicle-sections';
import { useFormVehicleId } from '../hooks/useFormVehicleId';
import { Wrapper, CloseIcon } from './ui';

const OFFLINE_BG = '#E93323';
const ONLINE_BG = '#479747';
const COMPLETED_BG = '#3994DE';

const typeMap = {
  online: {
    bg: ONLINE_BG,
    text: 'Back online, sync in process',
    Icon: OnlineIcon
  },
  offline: {
    bg: OFFLINE_BG,
    text: 'You’re offline but you can keep using Autoaxess. We’ll sync your data once you’re back online.',
    Icon: OfflineIcon
  },
  completed: {
    bg: COMPLETED_BG,
    text: 'Sync completed',
    Icon: CompletedIcon
  }
};

const wait = () =>
  new Promise<void>(res =>
    setTimeout(() => {
      res();
    }, 5000)
  );

export const Banner = () => {
  const [visibleType, setVisibleType] = useState<'offline' | 'online' | 'completed' | null>(null);

  let timeout: number;

  const [uiSsSyncing, setUiIsSyncing] = useState(false);

  const closeBanner = () => setVisibleType(null);

  const vehicleId = useFormVehicleId();

  const { refetch } = useGetOfflineFirstSectionsQuery(Number(vehicleId), { skip: !vehicleId });

  const syncManager = SyncManager.getInstance(vehicleId);

  const prevOffline = useRef<boolean | null>(null);
  const isOnline = useIsOnline();

  const timeoutRef = useRef<number | null>(null);

  const performSync = async () => {
    setVisibleType('online');
    setUiIsSyncing(true);

    await Promise.all([syncManager.start(), wait()]);
    await refetch();
    setVisibleType('completed');

    timeoutRef.current = setTimeout(() => {
      setUiIsSyncing(false);
      setVisibleType(null);
    }, 3000);
  };

  useEffect(() => {
    if (isOnline && prevOffline.current === false) {
      console.log('use effect double trigger???');
      performSync();
    }

    if (!isOnline) {
      syncManager.pause();
      setVisibleType('offline');
    }

    prevOffline.current = isOnline;
  }, [isOnline]);

  useEffect(() => {
    console.log('delete runs');
    db.delete({ disableAutoOpen: false });

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (visibleType === null) return null;

  const { bg, Icon, text } = typeMap[visibleType];

  return (
    <Wrapper bg={bg}>
      <Icon />
      <span>{text}</span>
      <CloseIcon onClick={closeBanner}>close</CloseIcon>
    </Wrapper>
  );
};
